import React, { useState, useContext, useEffect } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import LinkOffIcon from "@material-ui/icons/LinkOff";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import { getCurrentDomain } from "../../utils/tenant";
import { AuthShell, useAuthStyles } from "../Login/AuthShell";

const MIN_PASSWORD = 6;

const Invite = () => {
  const auth = useAuthStyles();
  const { token } = useParams();
  const { colorMode } = useContext(ColorModeContext);
  const { appLogoFavicon, appName } = colorMode;
  const { startSession } = useContext(AuthContext);

  const [status, setStatus] = useState("loading"); // loading | ready | notFound | expired | error
  const [invite, setInvite] = useState(null);
  const [form, setForm] = useState({ name: "", password: "", confirm: "", acceptTerms: false });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const displayName = appName || "Digite AI";
  const brand = (invite && invite.brand) || {};
  const termsUrl = brand.termsUrl && String(brand.termsUrl).trim() ? String(brand.termsUrl).trim() : "";
  const termsVersion = brand.termsVersion ? String(brand.termsVersion).trim() : "";

  useEffect(() => {
    let active = true;
    setStatus("loading");
    openApi
      .get(`/invite/${encodeURIComponent(token)}`, { params: { domain: getCurrentDomain() } })
      .then(({ data }) => {
        if (!active) return;
        setInvite(data || {});
        setStatus("ready");
      })
      .catch((err) => {
        if (!active) return;
        const code = err && err.response && err.response.status;
        setStatus(code === 404 ? "notFound" : code === 410 ? "expired" : "error");
      });
    return () => {
      active = false;
    };
  }, [token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const validate = () => {
    if (!form.password || form.password.length < MIN_PASSWORD) {
      toast.error(i18n.t("invite.errors.passwordMin", { min: MIN_PASSWORD }));
      return false;
    }
    if (form.password !== form.confirm) {
      toast.error(i18n.t("invite.errors.passwordMismatch"));
      return false;
    }
    if (termsUrl && !form.acceptTerms) {
      toast.error(i18n.t("terms.mustAccept"));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = { password: form.password };
      if (form.name && form.name.trim()) payload.name = form.name.trim();
      if (termsUrl) payload.acceptTerms = true;
      const { data } = await openApi.post(`/invite/${encodeURIComponent(token)}/accept`, payload, {
        params: { domain: getCurrentDomain() },
      });
      toast.success(i18n.t("invite.toasts.accepted"));
      // Mesmo caminho do login: grava token, popula usuário e vai para /tickets
      startSession(data);
    } catch (err) {
      const code = err && err.response && err.response.status;
      if (code === 404) setStatus("notFound");
      else if (code === 410) setStatus("expired");
      else toastError(err);
      setSubmitting(false);
    }
  };

  const renderError = () => {
    const key = status === "notFound" ? "notFound" : status === "expired" ? "expired" : "generic";
    return (
      <div className={auth.state}>
        <div className={auth.stateIcon}>
          <LinkOffIcon />
        </div>
        <p className={auth.stateTitle}>{i18n.t(`invite.${key}.title`)}</p>
        <p className={auth.stateText}>{i18n.t(`invite.${key}.message`)}</p>
        <Link component={RouterLink} to="/login" className={auth.link}>
          {i18n.t("invite.goToLogin")}
        </Link>
      </div>
    );
  };

  const icon = (Icon) => (
    <InputAdornment position="start">
      <Icon className={auth.fieldIcon} />
    </InputAdornment>
  );

  const renderForm = () => (
    <form noValidate onSubmit={handleSubmit} className={auth.form}>
      <div className={auth.grid}>
        <div className={`${auth.field} ${auth.full}`}>
          <label htmlFor="email" className={auth.label}>
            {i18n.t("invite.form.email")}
          </label>
          <TextField
            variant="outlined"
            fullWidth
            id="email"
            name="email"
            value={invite.adminEmail || ""}
            disabled
            className={auth.textField}
            InputProps={{ readOnly: true, startAdornment: icon(MailOutlineIcon) }}
          />
        </div>

        <div className={`${auth.field} ${auth.full}`}>
          <label htmlFor="name" className={auth.label}>
            {i18n.t("invite.form.name")}
          </label>
          <TextField
            variant="outlined"
            fullWidth
            id="name"
            name="name"
            autoFocus
            autoComplete="name"
            placeholder={i18n.t("invite.form.namePlaceholder")}
            value={form.name}
            onChange={handleChange}
            className={auth.textField}
            InputProps={{ startAdornment: icon(PersonOutlineIcon) }}
          />
        </div>

        <div className={auth.field}>
          <label htmlFor="password" className={auth.label}>
            {i18n.t("invite.form.password")}
          </label>
          <TextField
            variant="outlined"
            required
            fullWidth
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            placeholder="••••••••"
            value={form.password}
            onChange={handleChange}
            className={auth.textField}
            InputProps={{
              startAdornment: icon(LockOutlinedIcon),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((s) => !s)}
                    edge="end"
                    size="small"
                    className={auth.eyeBtn}
                    aria-label="toggle password visibility"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <span className={auth.help}>{i18n.t("invite.form.passwordHelper", { min: MIN_PASSWORD })}</span>
        </div>

        <div className={auth.field}>
          <label htmlFor="confirm" className={auth.label}>
            {i18n.t("invite.form.confirm")}
          </label>
          <TextField
            variant="outlined"
            required
            fullWidth
            id="confirm"
            name="confirm"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            placeholder="••••••••"
            value={form.confirm}
            onChange={handleChange}
            className={auth.textField}
            error={Boolean(form.confirm) && form.confirm !== form.password}
            InputProps={{ startAdornment: icon(LockOutlinedIcon) }}
          />
        </div>

        {termsUrl && (
          <div className={auth.full}>
            <FormControlLabel
              className={auth.terms}
              control={
                <Checkbox name="acceptTerms" color="primary" checked={form.acceptTerms} onChange={handleChange} />
              }
              label={
                <span className={auth.termsLabel} style={{ display: "block" }}>
                  {i18n.t("terms.acceptPrefix")}{" "}
                  <Link href={termsUrl} target="_blank" rel="noopener noreferrer" className={auth.link}>
                    {i18n.t("terms.acceptLink")}
                  </Link>
                  {termsVersion ? ` (${i18n.t("terms.version").toLowerCase()} ${termsVersion})` : ""}
                </span>
              }
            />
          </div>
        )}
      </div>

      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        disabled={submitting}
        className={submitting ? `${auth.submit} ${auth.submitBusy}` : auth.submit}
        endIcon={submitting ? null : <ArrowForwardIcon />}
      >
        {submitting ? <CircularProgress size={20} color="inherit" /> : i18n.t("invite.buttons.submit")}
      </Button>

      <div className={auth.links}>
        <Link component={RouterLink} to="/login" className={auth.link}>
          {i18n.t("invite.alreadyHaveAccount")}
        </Link>
      </div>
    </form>
  );

  return (
    <>
      <Helmet>
        <title>{`${i18n.t("invite.title")} · ${displayName}`}</title>
        <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
      </Helmet>
      <CssBaseline />

      <AuthShell>
        {appName && <p className={auth.eyebrow}>{appName}</p>}
        <h1 className={auth.title}>{i18n.t("invite.title")}</h1>
        {status === "ready" && invite && (
          <p className={auth.subtitle}>
            {i18n.t("invite.invitedTo")}{" "}
            <span className={auth.strong}>{invite.companyName}</span>
            <br />
            {i18n.t("invite.instruction")}
          </p>
        )}

        {status === "loading" && (
          <div className={auth.state}>
            <CircularProgress size={28} />
            <p className={auth.stateText}>{i18n.t("invite.loading")}</p>
          </div>
        )}
        {status === "ready" && invite && renderForm()}
        {(status === "notFound" || status === "expired" || status === "error") && renderError()}
      </AuthShell>
    </>
  );
};

export default Invite;
